import type { CreateInvitationInput } from "@handoverpro/shared";

import {
  createInvitation as sdkCreateInvitation,
  listInvitations as sdkListInvitations,
  previewInvitation as sdkPreviewInvitation,
  revokeInvitation as sdkRevokeInvitation,
  type InvitationDto,
  type InvitationPreviewDto,
} from "~/lib/api";

/**
 * Network layer for ward email invitations (V2-E3.3, HAN-88). Wraps the
 * generated SDK calls (HAN-85's `/wards/:wardId/invitations` + the public
 * `/invitations/:token`) in discriminated-union results so the screens stay free
 * of HTTP-status details — mirrors `~/lib/ward-members`. The admin paths are all
 * `@RequireWardAdmin`; `previewInvitation` is public (the invited-register flow).
 */

export type FetchResult<T> = { status: "ok"; data: T } | { status: "error"; message: string };

export type InvitationResult =
  | { status: "ok"; invitation: InvitationDto }
  | { status: "error"; message: string };

export type RevokeResult = { status: "ok" } | { status: "error"; message: string };

/** A ward's pending (not yet accepted/revoked/expired) invitations, newest first. */
export async function fetchPendingInvitations(
  wardId: string,
): Promise<FetchResult<InvitationDto[]>> {
  const result = await sdkListInvitations({ path: { wardId } });
  if (result.error || !result.data) {
    return {
      status: "error",
      message: `Couldn't load the pending invites (${result.response.status}).`,
    };
  }
  return { status: "ok", data: result.data };
}

/**
 * Invite an email to the ward at a membership role. Inviting an address that
 * already has a live pending invite returns that invite (the API dedupes), so a
 * double-invite is a no-op, not an error.
 */
export async function createWardInvitation(
  wardId: string,
  input: CreateInvitationInput,
): Promise<InvitationResult> {
  const result = await sdkCreateInvitation({ path: { wardId }, body: input });
  if (result.error || !result.data) {
    return { status: "error", message: messageForCreateStatus(result.response.status) };
  }
  return { status: "ok", invitation: result.data };
}

function messageForCreateStatus(status: number): string {
  switch (status) {
    case 400:
      return "Enter a valid email address.";
    case 403:
      return "Only a ward admin can invite people.";
    case 404:
      return "This ward no longer exists.";
    default:
      return "Something went wrong sending the invite. Please try again.";
  }
}

/**
 * Revoke a pending invite so its link stops working. Revoking an
 * already-revoked or expired invite is idempotent (the API returns 204); a 409
 * means the invitee has already accepted — they're a member now, nothing to do.
 */
export async function revokeWardInvitation(
  wardId: string,
  invitationId: string,
): Promise<RevokeResult> {
  const result = await sdkRevokeInvitation({ path: { wardId, invitationId } });
  // 204 No Content: the SDK leaves `data` undefined, so success is "no error".
  if (result.error) {
    return { status: "error", message: messageForRevokeStatus(result.response.status) };
  }
  return { status: "ok" };
}

function messageForRevokeStatus(status: number): string {
  switch (status) {
    case 403:
      return "Only a ward admin can revoke invites.";
    case 404:
      return "That invitation no longer exists.";
    case 409:
      return "That invitation has already been accepted.";
    default:
      return "Something went wrong revoking the invite. Please try again.";
  }
}

/**
 * Resolve an accept-link token to its preview (ward, invited email, role,
 * effective status) for the invited-register screen. Public — no auth needed; a
 * 404 means the token matches no invitation.
 */
export async function previewInvitation(token: string): Promise<FetchResult<InvitationPreviewDto>> {
  const result = await sdkPreviewInvitation({ path: { token } });
  if (result.error || !result.data) {
    return {
      status: "error",
      message:
        result.response.status === 404
          ? "This invitation link is invalid."
          : "Couldn't load this invitation. Please try again.",
    };
  }
  return { status: "ok", data: result.data };
}
